<template>
  <div :id="`${source}_quill_editor_content_renderer`">
    <span
      :class="[
        'message_body_wrapper [&>ul]:list-disc [&>ul]:pl-10 [&>ol]:list-decimal [&>ol]:pl-10 whitespace-pre-wrap break-words overflow-auto',
        extraStyles,
      ]"
      v-html="content"
    />
  </div>
</template>

<script setup>
const props = defineProps({
  source: { type: String, required: true },
  content: { type: String, required: true },
  extraStyles: { type: String, required: false },
});
</script>

<style scoped>
.message_body_wrapper {
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
  height: fit-content;
  white-space: pre-wrap;
  max-height: 57vh;
  overflow-y: auto;
}

.message_body_wrapper a {
  word-break: break-all;
  color: var(--link-color);
  text-decoration: underline;
}

.message_body_wrapper :deep(ul) {
  @apply list-none pl-10;
}

.message_body_wrapper :deep(ol li[data-list="bullet"]) {
  @apply list-none;
  counter-increment: none !important;
}

.message_body_wrapper :deep(ol li[data-list="bullet"])::before {
  content: "•" !important;
  @apply absolute left-0;
}

.message_body_wrapper :deep(ol li.ql-indent-1[data-list="bullet"]) {
  @apply pl-[2.4em];
}

.message_body_wrapper :deep(ol li.ql-indent-1[data-list="bullet"])::before {
  content: "•" !important;
  @apply absolute left-[1.2em];
}

.message_body_wrapper :deep(ol li.ql-indent-2[data-list="bullet"]) {
  @apply pl-[3.6em];
}

.message_body_wrapper :deep(ol li.ql-indent-2[data-list="bullet"])::before {
  content: "•" !important;
  @apply absolute left-[2.4em];
}

.message_body_wrapper :deep(ol li.ql-indent-3[data-list="bullet"]) {
  @apply pl-[4.8em];
}

.message_body_wrapper :deep(ol li.ql-indent-3[data-list="bullet"])::before {
  content: "•" !important;
  @apply absolute left-[3.6em];
}

.message_body_wrapper :deep(ul li[data-list="ordered"]) {
  list-style-type: decimal !important;
}

.message_body_wrapper :deep(ol) {
  counter-reset: main-counter;
  list-style-type: none;
}

.message_body_wrapper :deep(ol li),
.message_body_wrapper :deep(ul li) {
  @apply relative pl-[1.2em];
}

.message_body_wrapper :deep(ul li)::before {
  content: "•";
  @apply absolute left-0;
}

.message_body_wrapper :deep(ol li:not([class*="ql-indent"]):not([data-list="bullet"])) {
  counter-increment: main-counter;
  counter-reset: sub-counter;
}

.message_body_wrapper :deep(ol li:not([class*="ql-indent"]):not([data-list="bullet"]))::before {
  content: counter(main-counter) ". ";
  @apply absolute left-0 font-normal;
}

.message_body_wrapper :deep(ol li.ql-indent-1:not([data-list="bullet"])) {
  counter-increment: sub-counter;
  counter-reset: sub-sub-counter;
  @apply pl-[2.4em];
}

.message_body_wrapper :deep(ol li.ql-indent-1:not([data-list="bullet"]))::before {
  content: counter(sub-counter, lower-alpha) ". ";
  @apply absolute left-[1.2em] font-normal;
}

.message_body_wrapper :deep(ol li.ql-indent-2:not([data-list="bullet"])) {
  counter-increment: sub-sub-counter;
  counter-reset: sub-sub-sub-counter;
  @apply relative pl-[3.6em];
}

.message_body_wrapper :deep(ol li.ql-indent-2:not([data-list="bullet"]))::before {
  content: counter(sub-sub-counter, lower-roman) ". ";
  @apply absolute left-[2.4em] font-normal;
}

.message_body_wrapper :deep(ol li.ql-indent-3:not([data-list="bullet"])) {
  counter-increment: sub-sub-sub-counter;
  counter-reset: sub-sub-sub-sub-counter;
  @apply relative pl-[4.8em];
}

.message_body_wrapper :deep(ol li.ql-indent-3:not([data-list="bullet"]))::before {
  content: counter(sub-sub-sub-counter, decimal) ". ";
  @apply absolute left-[3.6em] font-normal;
}

.message_body_wrapper :deep(ol li.ql-indent-4:not([data-list="bullet"])) {
  counter-increment: sub-sub-sub-sub-counter;
  @apply relative pl-[6em];
}

.message_body_wrapper :deep(ol li.ql-indent-4:not([data-list="bullet"]))::before {
  content: counter(sub-sub-sub-sub-counter, disc) " ";
  @apply absolute left-[4.8em] font-normal;
}

.message_body_wrapper :deep(ul li.ql-indent-1) {
  @apply pl-[2.4em];
}

.message_body_wrapper :deep(ul li.ql-indent-2) {
  @apply pl-[3.6em];
}

.message_body_wrapper :deep(ul li.ql-indent-3) {
  @apply pl-[4.8em];
}

.message_body_wrapper :deep(ul li.ql-indent-4) {
  @apply pl-[6em];
}

.message_body_wrapper :deep(.preview_ai_variable) {
  vertical-align: middle;
}

.message_body_wrapper :deep(h1) {
  font-size: 2rem;
  margin-top: 1rem;
  line-height: 1.2;
}

.message_body_wrapper :deep(h2) {
  font-size: 1.5rem;
  margin-top: 0.75rem;
  line-height: 1.3;
}

.message_body_wrapper :deep(h3) {
  font-size: 1.25rem;
  margin-top: 0.5rem;
  line-height: 1.4;
}

.message_body_wrapper :deep(h4) {
  font-size: 1.125rem;
  margin-top: 0.5rem;
  line-height: 1.4;
}

.message_body_wrapper :deep(h5) {
  font-size: 1rem;
  margin-top: 0.25rem;
  line-height: 1.5;
}

.message_body_wrapper :deep(h6) {
  font-size: 0.875rem;
  margin-top: 0.25rem;
  line-height: 1.5;
}

.message_body_wrapper :deep(p) {
  line-height: 1.5;
  white-space: pre-wrap !important;
}

.message_body_wrapper :deep(li) {
  white-space: pre-wrap !important;
}

.message_body_wrapper :deep(div) {
  white-space: pre-wrap !important;
}

.message_body_wrapper :deep(span) {
  white-space: pre-wrap !important;
}

.message_body_wrapper :deep(strong) {
  font-weight: bold;
}

.message_body_wrapper :deep(em) {
  font-style: italic;
}
</style>
